import { useEffect } from 'react';

/**
 * Lightweight SEO hook — sets <title> and meta tags on mount, restores on
 * unmount. Avoids react-helmet dependency for such a small surface area.
 *
 * Usage:
 *   useSEO({
 *     title: 'Producto X — AutoMarket Colombia',
 *     description: 'Descripción del producto',
 *     image: 'https://...',
 *     canonical: '/producto/slug',
 *   });
 */
const DEFAULT_TITLE = 'AutoMarket Colombia | Marketplace de repuestos para vehículos';

function setMeta(selector, attr, name, content) {
  if (!content) return null;
  let el = document.head.querySelector(selector);
  const created = !el;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  const prev = el.getAttribute('content');
  el.setAttribute('content', content);
  return { el, prev, created };
}

function setCanonical(href) {
  if (!href) return null;
  let el = document.head.querySelector('link[rel="canonical"]');
  const created = !el;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  const prev = el.getAttribute('href');
  el.setAttribute('href', href);
  return { el, prev, created };
}

export function useSEO({ title, description, image, canonical, jsonLd } = {}) {
  // Clave estable: el objeto jsonLd suele recrearse en cada render.
  const jsonLdText = jsonLd ? JSON.stringify(jsonLd) : null;
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const changes = [
      setMeta('meta[name="description"]', 'name', 'description', description),
      setMeta('meta[property="og:title"]', 'property', 'og:title', title),
      setMeta('meta[property="og:description"]', 'property', 'og:description', description),
      setMeta('meta[property="og:image"]', 'property', 'og:image', image),
      setMeta('meta[property="og:type"]', 'property', 'og:type', 'website'),
      setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', image ? 'summary_large_image' : 'summary'),
      setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', title),
      setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', description),
      setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', image),
      setCanonical(canonical ? new URL(canonical, window.location.origin).toString() : window.location.href),
    ].filter(Boolean);

    let ldScript = null;
    if (jsonLdText) {
      ldScript = document.createElement('script');
      ldScript.type = 'application/ld+json';
      ldScript.text = jsonLdText;
      document.head.appendChild(ldScript);
    }

    return () => {
      document.title = prevTitle || DEFAULT_TITLE;
      changes.forEach((c) => {
        if (!c) return;
        if (c.created) c.el.remove();
        else if (c.prev != null) {
          // restore previous value on the existing tag
          if (c.el.tagName === 'LINK') c.el.setAttribute('href', c.prev);
          else c.el.setAttribute('content', c.prev);
        }
      });
      if (ldScript) ldScript.remove();
    };
  }, [title, description, image, canonical, jsonLdText]);
}
